import { useState } from 'react';
import { Country, State, City } from 'country-state-city';
import { createInquiry } from '../../services/firestoreService';

const countryOptions = Country.getAllCountries();

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  country: '',
  state: '',
  city: '',
  subject: '',
  message: '',
  followChannels: false,
  acceptTerms: false
};

const ContactForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'country') {
      setFormData(prev => ({ ...prev, country: value, state: '', city: '' }));
      return;
    }

    if (name === 'state') {
      setFormData(prev => ({ ...prev, state: value, city: '' }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const states = formData.country ? State.getStatesOfCountry(formData.country) : [];
  const cities = formData.country && formData.state ? City.getCitiesOfState(formData.country, formData.state) : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (!formData.followChannels || !formData.acceptTerms) {
      setLoading(false);
      setError('Please confirm both requirements before submitting.');
      return;
    }

    const selectedCountry = countryOptions.find(country => country.isoCode === formData.country);
    const selectedState = states.find(state => state.isoCode === formData.state);
    const selectedCity = cities.find(city => city.name === formData.city);

    const result = await createInquiry({
      ...formData,
      country: selectedCountry?.name || '',
      state: selectedState?.name || '',
      city: selectedCity?.name || formData.city,
      status: 'new',
      createdAt: new Date()
    });

    if (result.success) {
      setSuccess(true);
      setFormData(initialFormData);
      setTimeout(() => setSuccess(false), 5000);
    } else {
      setError(result.error || 'Failed to submit inquiry. Please try again.');
    }

    setLoading(false);
  };

  return (
    <section className="section section-form">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Have Questions? Get in Touch!</h2>
          <p className="section-description">
            We're here to help you start your winning journey. Submit your inquiry and our team will respond within 24 hours.
          </p>
        </div>

        <div className="form-wrapper">
          <div className="form-benefits">
            <h3 className="benefits-heading">Why Contact Us?</h3>
            
            <div className="benefit-item-form">
              <i data-lucide="check-circle" className="benefit-icon-form"></i>
              <div>
                <h4 className="benefit-title-form">Quick Response</h4>
                <p className="benefit-text-form">Get answers to your questions within 24 hours from our dedicated support team.</p>
              </div>
            </div>

            <div className="benefit-item-form">
              <i data-lucide="shield-check" className="benefit-icon-form"></i>
              <div>
                <h4 className="benefit-title-form">Personalized Guidance</h4>
                <p className="benefit-text-form">Receive customized advice on how to maximize your chances of winning.</p>
              </div>
            </div>

            <div className="benefit-item-form">
              <i data-lucide="gift" className="benefit-icon-form"></i>
              <div>
                <h4 className="benefit-title-form">Exclusive Updates</h4>
                <p className="benefit-text-form">Be the first to know about new prize opportunities and special offers.</p>
              </div>
            </div>

            <div className="benefit-item-form">
              <i data-lucide="users" className="benefit-icon-form"></i>
              <div>
                <h4 className="benefit-title-form">Join Our Community</h4>
                <p className="benefit-text-form">Connect with thousands of winners and become part of the Offer Lifetime# family.</p>
              </div>
            </div>

            <div className="trust-badge">
              <i data-lucide="award" className="trust-icon"></i>
              <p>Trusted by 10,000+ Happy Customers</p>
            </div>
          </div>

          <div className="inquiry-form">
            {success && (
              <div style={{ background: '#d4edda', color: '#155724', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                Thank you! Your inquiry has been submitted successfully. We'll get back to you within 24 hours.
              </div>
            )}

            {error && (
              <div style={{ background: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+91 XXXXX XXXXX"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a country</option>
                  {countryOptions.map(country => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="state">State *</label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  disabled={!states.length}
                >
                  <option value="">{formData.country ? 'Select a state' : 'Choose country first'}</option>
                  {states.map(state => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="city">City *</label>
                <select
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={!cities.length}
                >
                  <option value="">{formData.state ? 'Select a city' : 'Choose state first'}</option>
                  {cities.map(city => (
                    <option key={`${city.name}-${city.latitude}-${city.longitude}`} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a topic</option>
                  <option value="vip-token">VIP Token Purchase</option>
                  <option value="prize-info">Prize Information</option>
                  <option value="winner-verification">Winner Verification</option>
                  <option value="payment">Payment & Refund</option>
                  <option value="general">General Inquiry</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  placeholder="Tell us how we can help you..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <label className="form-consent">
                <input
                  type="checkbox"
                  name="followChannels"
                  checked={formData.followChannels}
                  onChange={handleChange}
                  required
                />
                <span>It is mandatory to follow our channel on Facebook and Instagram and subscribe our YouTube channel.</span>
              </label>

              <label className="form-consent">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  required
                />
                <span>Our terms &amp; conditions apply.</span>
              </label>

              <button type="submit" className="btn btn-primary btn-submit" disabled={loading}>
                <i data-lucide="send"></i>
                {loading ? 'Submitting...' : 'Submit Inquiry'}
              </button>

              <p className="form-note">* All fields are required. We respect your privacy and will never share your information.</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
